import { useState, useEffect } from "react"
import { Plus, HelpCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Label,
} from "@f2e/ui/components/base"

import { Permission, Workspace, WorkspaceApiKey } from "@/types"
import { useToast } from "@/hooks"
import { PermissionButton, PermissionSwitch } from "@/components/Auth"
import {
  getApiErrorData,
  getUserFacingApiErrorMessage,
  getUserFacingApiErrorToastProps,
} from "@/lib/apiErrors"
import {
  useGetWorkspaceApiKeysApi,
  useIssueWorkspaceApiKeyApi,
  useUpdateApiKeyClientsBypassWhitelistApi,
  workspaceApiKeysResponseToApiKeys,
  workspaceApiKeyResponseToWorkspaceApiKey,
  FetchStatus,
} from "@/hooks/api"

import { ApiKeysTable } from "./ApiKeysTable"
import { IssueApiKeyDialog } from "./IssueApiKeyDialog"
import { ApiKeyCreatedDialog } from "./ApiKeyCreatedDialog"

export interface ApiKeysTabProps {
  workspace: Workspace
}

export const ApiKeysTab = ({ workspace }: ApiKeysTabProps) => {
  const { t } = useTranslation()
  const { successToast, alertToast } = useToast()

  const [apiKeys, setApiKeys] = useState<WorkspaceApiKey[]>([])
  const [showIssueDialog, setShowIssueDialog] = useState(false)
  const [showCreatedDialog, setShowCreatedDialog] = useState(false)
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<{
    apiKey: string
    name: string
    role: WorkspaceApiKey["role"]
  } | null>(null)
  const [bypassWhitelist, setBypassWhitelist] = useState(
    workspace.apiKeyClientsBypassWhitelist ?? false
  )

  // APIs
  const {
    data: getKeysRespData,
    fetchFn: getApiKeys,
    error: getKeysRespError,
  } = useGetWorkspaceApiKeysApi(workspace.organizationId || "", workspace.id)
  const {
    data: issueKeyRespData,
    fetchFn: issueApiKey,
    status: issueKeyRespStatus,
    error: issueKeyRespError,
  } = useIssueWorkspaceApiKeyApi(workspace.organizationId || "", workspace.id)
  const {
    fetchFn: updateBypassWhitelist,
    status: updateBypassStatus,
    error: updateBypassError,
  } = useUpdateApiKeyClientsBypassWhitelistApi(
    workspace.organizationId || "",
    workspace.id
  )

  // Sync bypass whitelist state with workspace prop
  useEffect(() => {
    setBypassWhitelist(workspace.apiKeyClientsBypassWhitelist ?? false)
  }, [workspace.apiKeyClientsBypassWhitelist])

  // Load API keys on mount
  useEffect(() => {
    getApiKeys()
  }, [getApiKeys])

  // Handle get keys response
  useEffect(() => {
    if (!getKeysRespData || !getKeysRespData.data) return
    const keys = workspaceApiKeysResponseToApiKeys(getKeysRespData.data)
    setApiKeys(keys)
  }, [getKeysRespData])

  // Handle get keys error
  useEffect(() => {
    if (!getKeysRespError) return
    console.error(
      "Failed to load API keys.",
      getApiErrorData(getKeysRespError)?.message || getKeysRespData?.message
    )
    alertToast({
      title: t("workspace.apiKeys.messages.error.loadKeys.title"),
      description: getUserFacingApiErrorMessage(
        getKeysRespError,
        t,
        t("workspace.apiKeys.messages.error.loadKeys.description")
      ),
    })
  }, [getKeysRespError, getKeysRespData, alertToast, t])

  // Handle issue key response
  useEffect(() => {
    if (!issueKeyRespData || !issueKeyRespData.data) return

    const newKey = workspaceApiKeyResponseToWorkspaceApiKey(
      issueKeyRespData.data
    )
    setApiKeys((prevKeys) => [newKey, ...prevKeys])
    setNewlyCreatedKey({
      apiKey: newKey.apiKey || "",
      name: newKey.name,
      role: newKey.role,
    })
    setShowIssueDialog(false)
    setShowCreatedDialog(true)
    successToast({
      title: t("workspace.apiKeys.messages.issued.title"),
      description: t("workspace.apiKeys.messages.issued.description"),
    })
  }, [issueKeyRespData, successToast, t])

  // Handle issue key error
  useEffect(() => {
    if (!issueKeyRespError) return
    console.error(
      "Failed to create API key.",
      getApiErrorData(issueKeyRespError)?.message || issueKeyRespData?.message
    )
    alertToast({
      title: t("workspace.apiKeys.messages.error.issueKey.title"),
      description: getUserFacingApiErrorMessage(
        issueKeyRespError,
        t,
        t("workspace.apiKeys.messages.error.issueKey.description")
      ),
    })
  }, [issueKeyRespError, issueKeyRespData, alertToast, t])

  const handleIssueApiKey = async (data: {
    name: string
    role: WorkspaceApiKey["role"]
    expiresAt?: Date
  }) => {
    await issueApiKey({
      jsonBody: {
        name: data.name,
        role: data.role,
        expires_at: data.expiresAt?.toISOString(),
      },
    })
  }

  const handleRevokeApiKey = () => {
    getApiKeys()
  }

  const handleToggleBypassWhitelist = async (checked: boolean) => {
    setBypassWhitelist(checked)
    try {
      await updateBypassWhitelist({
        jsonBody: {
          bypass_whitelist: checked,
        },
      })
      successToast({
        title: t("workspace.apiKeys.bypassWhitelist.updated.title"),
        description: t("workspace.apiKeys.bypassWhitelist.updated.description"),
      })
    } catch (error) {
      setBypassWhitelist(!checked)
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("workspace.apiKeys.bypassWhitelist.error.title"),
          fallbackDescription: t(
            "workspace.apiKeys.bypassWhitelist.error.description"
          ),
        }),
      })
    }
  }

  // Handle update bypass error
  useEffect(() => {
    if (!updateBypassError) return
    console.error(
      "Failed to update bypass whitelist setting.",
      updateBypassError
    )
  }, [updateBypassError])

  return (
    <div className="space-y-6">
      {apiKeys.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">
                {t("workspace.apiKeys.title")}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t("workspace.apiKeys.description")}
              </p>
            </div>
            <PermissionButton
              permission={Permission.WsUpdateResources}
              workspace={workspace}
              disabledTooltip={t("common.noPermission")}
              onClick={() => setShowIssueDialog(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              {t("workspace.apiKeys.issueNew")}
            </PermissionButton>
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center gap-2">
              <Label
                htmlFor="bypass-whitelist-toggle"
                className="cursor-pointer"
              >
                {t("workspace.apiKeys.bypassWhitelist.label")}
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-slate-500 dark:text-slate-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{t("workspace.apiKeys.bypassWhitelist.tooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <PermissionSwitch
              id="bypass-whitelist-toggle"
              permission={Permission.WsUpdateResources}
              workspace={workspace}
              disabledTooltip={t("common.noPermission")}
              checked={bypassWhitelist}
              onCheckedChange={handleToggleBypassWhitelist}
              disabled={updateBypassStatus === FetchStatus.Loading}
            />
          </div>
        </>
      )}

      <ApiKeysTable
        workspace={workspace}
        apiKeys={apiKeys}
        onRevoke={handleRevokeApiKey}
        onIssueNew={() => setShowIssueDialog(true)}
      />

      <IssueApiKeyDialog
        open={showIssueDialog}
        onOpenChange={setShowIssueDialog}
        onSubmit={handleIssueApiKey}
        isSubmitting={issueKeyRespStatus === FetchStatus.Loading}
      />

      {newlyCreatedKey && (
        <ApiKeyCreatedDialog
          open={showCreatedDialog}
          onOpenChange={setShowCreatedDialog}
          apiKey={newlyCreatedKey.apiKey}
          apiKeyName={newlyCreatedKey.name}
          role={newlyCreatedKey.role}
        />
      )}
    </div>
  )
}

export default ApiKeysTab
